import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Chip, Typography, IconButton, Stack, Avatar, Divider,
  Alert, CircularProgress, Button, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  getPublicRestaurante,
  listPublicCardapios,
  listPublicCategorias,
  listPublicItens,
  criarOuObterPedidoPublic,
  adicionarItemPedidoPublic,
  atualizarItemPedidoPublic,
  enviarPedidoPublic,
  listPublicItemComplementos,
  listPublicItemOptionGroups,
  removerItemPedidoPublic,
  getPedidoPublic,
  criarComandaPublic,
  getComandaPublic,
  fecharComandaPublic,
} from '../../../../services/publicApi';

import ItemPopup from './ItemPopup';
import { isCardapioAvailable } from '../utils/cardapioAvailability';

// --- ESTILOS/HELPERS ---
const categoryHeaderSx = {
  bgcolor: 'grey.100', px: 2, py: 1.5, borderRadius: 1.5, mb: 2,
  borderBottom: '1px solid', borderColor: 'grey.200',
};
const heroHeight = { xs: 180, sm: 240, md: 300 };
const fallbackHeroGradient = 'linear-gradient(90deg,#5c6bc0,#7e57c2)';
const themes = {
  modern: {
    background: '#ffffff',
    textPrimary: 'text.primary',
    textSecondary: 'text.secondary',
    categoryBg: 'grey.100',
    categoryBorder: 'grey.200',
    accent: 'primary.main',
    price: 'text.primary',
    fontFamily: 'inherit',
    heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0.22), rgba(0,0,0,0.08))',
  },
  retro: {
    background: '#fff7e6',
    textPrimary: '#1c274c',
    textSecondary: '#1f3b70',
    categoryBg: '#ffe8cc',
    categoryBorder: '#f26b3a',
    accent: '#1f4fbf',
    price: '#1f4fbf',
    fontFamily: '"Georgia","Times New Roman",serif',
    heroOverlay: 'linear-gradient(180deg, rgba(255,247,230,0.65), rgba(255,247,230,0.35))',
  },
};

const getNome = (ent) => ent?.nome || '';
const getDescricao = (ent) => ent?.descricao || '';
const sortByOrder = (a, b) => (a.ordem ?? Infinity) - (b.ordem ?? Infinity);
const PUBLIC_MENU_CHANNELS = new Set(['delivery', 'in_person']);
const normalizePublicMenuChannel = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return PUBLIC_MENU_CHANNELS.has(normalized) ? normalized : 'delivery';
};
const getPublicChannelLabel = (value) => (
  normalizePublicMenuChannel(value) === 'in_person' ? 'salao' : 'delivery'
);

const normalizePedidoItem = (it) => ({
  id: it.id || it.pedido_item_id || `local-${Date.now()}`,
  pedido_item_id: it.id || it.pedido_item_id || null,
  item_id: it.item_id,
  nome: it.item?.nome || it.nome || '',
  preco: Number(it.preco_unit || it.item?.preco || 0),
  quantidade: it.quantidade || 1,
  observacao: it.observacao || '',
  complementos: (it.complementos || []).map((c) => ({
    id: c.complemento_id || c.id,
    nome: c.complemento?.nome || c.nome,
    preco: Number(c.preco || c.complemento?.preco || 0),
  })),
  opcoes: (it.opcoes || []).map((option) => ({
    id: option.opcao_id || option.id,
    nome: option.opcao_nome || option.nome || '',
    grupo_nome: option.grupo_nome || '',
    preco: Number(option.preco_total || 0),
  })),
});

const normalizePedidoFromApi = (pedidoApi) => {
  if (!pedidoApi) return { id: null, mesa: null, itens: [], status: 'aberto' };
  const itens = (pedidoApi.itens || []).map(normalizePedidoItem);
  return {
    id: pedidoApi.id,
    comanda_id: pedidoApi.comanda_id,
    mesa: pedidoApi.mesa,
    status: pedidoApi.status || 'aberto',
    itens,
  };
};
const calcularTotal = (pedidoData) => (pedidoData?.itens || []).reduce((acc, it) => {
  const compTotal = (it.complementos || []).reduce((a, c) => a + (c.preco || 0), 0);
  const optionTotal = (it.opcoes || []).reduce((a, option) => a + (option.preco || 0), 0);
  const base = (it.preco || 0) + compTotal + optionTotal;
  return acc + base * (it.quantidade || 1);
}, 0);

const COMANDA_TTL_MINUTES = 360; // 6h padrão
const isComandaAtiva = (c) => c && !['finalizado', 'cancelado', 'finalizada'].includes((c.status || '').toLowerCase());

// --- ITENS ---
const DefaultItem = ({ item, onClick, theme, layout }) => {
  const nome = getNome(item);
  const descricao = getDescricao(item);
  const temPreco = item.preco != null;
  const precoFormatado = temPreco
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.preco)
    : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 110px', sm: '1fr 120px' },
        alignItems: 'center',
        gap: { xs: 1.5, sm: 2.5 },
        py: 2.5,
        cursor: 'pointer',
      }}
    >
      <Box sx={{ pr: { xs: 0, sm: 1.5 }, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" fontWeight={700} mb={0.5} sx={{ color: theme.textPrimary, letterSpacing: layout === 'retro' ? '0.02em' : 0 }}>{nome}</Typography>
        {descricao && <Typography variant="body2" color={theme.textSecondary} sx={{ fontFamily: theme.fontFamily }}>{descricao}</Typography>}
        {temPreco && (
          <Typography
            variant="h6"
            fontWeight={700}
            color={theme.price}
            sx={{ mt: descricao ? 1 : 0.5 }}
          >
            {precoFormatado}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: '8px !important',
          aspectRatio: '1 / 1',
          overflow: 'hidden',
          bgcolor: 'grey.200',
          backgroundImage: item.foto_url ? `url("${item.foto_url}")` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          justifySelf: 'end',
          alignSelf: 'center',
        }}
      />
    </Box>
  );
};

// --- PRINCIPAL ---
export default function PublicMenu() {
  const [cardapios, setCardapios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [itens, setItens] = useState({});
  const [selectedCardapioId, setSelectedCardapioId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemComplementos, setSelectedItemComplementos] = useState([]);
  const [selectedItemOptionGroups, setSelectedItemOptionGroups] = useState([]);
  const [pedido, setPedido] = useState({ id: null, mesa: null, itens: [], status: 'aberto' });
  const [carrinho, setCarrinho] = useState([]);
  const [restaurante, setRestaurante] = useState(null);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const headerRef = React.useRef(null);
  const historicoRef = React.useRef(null);
  const carrinhoRef = React.useRef(null);
  const [currentTab, setCurrentTab] = useState('home');
  const [historicoPedidos, setHistoricoPedidos] = useState([]);
  const [comanda, setComanda] = useState(null);
  const comandaRef = React.useRef(null);
  const [profile, setProfile] = useState({ nome: '', telefone: '' });
  const [showComandaDialog, setShowComandaDialog] = useState(false);
  const [showFecharComandaConfirm, setShowFecharComandaConfirm] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [profileDone, setProfileDone] = useState(false);
  const [availabilityNow, setAvailabilityNow] = useState(() => new Date());

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  
  // Parâmetros de URL
  const mesaParam = searchParams.get('mesa');
  const cardapioIdParam = searchParams.get('cardapioId');
  const tokenParam = searchParams.get('token');
  const canalSelecionado = useMemo(
    () => normalizePublicMenuChannel(searchParams.get('canal')),
    [searchParams]
  );
  const channelLabel = getPublicChannelLabel(canalSelecionado);
  const viewParam = searchParams.get('view'); // NOVO: captura o parâmetro view

  const profileFlagKey = tokenParam ? `publicProfileDone_${tokenParam}` : null;

  // NOVO: Effect para forçar a aba 'home' se view=inicio for passado na URL
  useEffect(() => {
    if (viewParam === 'inicio') {
        setCurrentTab('home');
    }
  }, [viewParam]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setAvailabilityNow(new Date()), 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  // Carrega pedido salvo no localStorage
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('publicPedido');
      if (stored) {
        const parsed = JSON.parse(stored);
        setPedido(parsed);
      }
      const storedComanda = window.localStorage.getItem('publicComanda');
      if (storedComanda) {
        const parsedCom = JSON.parse(storedComanda);
        if (!parsedCom.expiresAt || parsedCom.expiresAt > Date.now()) {
          setComanda(parsedCom);
          comandaRef.current = parsedCom;
        } else {
          window.localStorage.removeItem('publicComanda');
          window.localStorage.removeItem('publicPedido');
          window.localStorage.removeItem('publicCarrinho');
          window.localStorage.removeItem('publicHistorico');
        }
      }
      const storedProfile = window.localStorage.getItem('publicProfile');
      if (storedProfile) {
        const parsedProfile = JSON.parse(storedProfile);
        setProfile({
          nome: parsedProfile?.nome || '',
          telefone: parsedProfile?.telefone || '',
        });
      }
      if (profileFlagKey) {
        const done = window.localStorage.getItem(profileFlagKey);
        setProfileDone(done === '1');
      } else {
        setProfileDone(false);
      }
      const storedHistory = window.localStorage.getItem('publicHistorico');
      if (storedHistory) {
        const parsedHist = JSON.parse(storedHistory);
        setHistoricoPedidos(Array.isArray(parsedHist) ? parsedHist : []);
      }
      const storedCart = window.localStorage.getItem('publicCarrinho');
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        setCarrinho(Array.isArray(parsedCart) ? parsedCart : []);
      }
      setHydrated(true);
    } catch {
      // ignora erros de parse
    }
  }, [profileFlagKey]);

  // Persiste pedido no localStorage quando muda
  useEffect(() => {
    try {
      window.localStorage.setItem('publicPedido', JSON.stringify(pedido));
    } catch {
      // ignore
    }
  }, [pedido]);

  // Persiste carrinho
  useEffect(() => {
    try {
      window.localStorage.setItem('publicCarrinho', JSON.stringify(carrinho));
    } catch {
      // ignore
    }
  }, [carrinho]);

  // Persiste histórico
  useEffect(() => {
    try {
      window.localStorage.setItem('publicHistorico', JSON.stringify(historicoPedidos));
    } catch {
      // ignore
    }
  }, [historicoPedidos]);
  // Persiste comanda
  useEffect(() => {
    try {
      if (comanda) {
        window.localStorage.setItem('publicComanda', JSON.stringify(comanda));
      }
    } catch {
      // ignore
    }
  }, [comanda]);
  useEffect(() => {
    comandaRef.current = comanda;
  }, [comanda]);
  useEffect(() => {
    try {
      window.localStorage.setItem('publicProfile', JSON.stringify(profile));
    } catch {
      // ignore
    }
  }, [profile]);

  const clearSessao = useCallback(() => {
    setComanda(null);
    setPedido({ id: null, mesa: null, itens: [], status: 'aberto' });
    setCarrinho([]);
    setHistoricoPedidos([]);
    setProfile({ nome: '', telefone: '' });
    try {
      window.localStorage.removeItem('publicComanda');
      window.localStorage.removeItem('publicPedido');
      window.localStorage.removeItem('publicCarrinho');
      window.localStorage.removeItem('publicHistorico');
      window.localStorage.removeItem('publicProfile');
    } catch {
      // ignore
    }
  }, []);

  const ensureComanda = useCallback(async () => {
    if (!mesaParam) return null;
    const hasProfile = !!profile?.nome?.trim();
    if (!tokenParam && !hasProfile) return null;
    try {
      const current = comandaRef.current;
      if (tokenParam) {
        const info = await getComandaPublic(slug, tokenParam);
        const normalized = {
          ...info,
          token: tokenParam,
          comanda_id: info?.comanda_id || info?.id,
          id: info?.comanda_id || info?.id,
          status: info?.status || 'aberta',
          expiresAt: current?.expiresAt || Date.now() + COMANDA_TTL_MINUTES * 60 * 1000,
        };
        // Para token, mesmo com comanda fechada mantemos os dados (não limpamos sessão)
        setComanda((prev) => {
          const same =
            prev &&
            prev.id === normalized.id &&
            prev.token === normalized.token &&
            prev.status === normalized.status &&
            prev.mesa === normalized.mesa;
          return same ? prev : normalized;
        });
        return normalized;
      }
      if (current?.token && !current?.comanda_id && !current?.id) {
        const info = await getComandaPublic(slug, current.token);
        const normalized = {
          ...info,
          token: current.token,
          comanda_id: info?.comanda_id || info?.id,
          id: info?.comanda_id || info?.id,
          status: info?.status || current?.status || 'aberta',
          expiresAt: Date.now() + COMANDA_TTL_MINUTES * 60 * 1000,
        };
        if (!isComandaAtiva(normalized)) {
          clearSessao();
          return null;
        }
        setComanda(normalized);
        return normalized;
      }
      if (current?.comanda_id || current?.id) {
        if (!isComandaAtiva(current) || (current.expiresAt && current.expiresAt <= Date.now())) {
          clearSessao();
          return null;
        }
        const refreshed = { ...current, expiresAt: Date.now() + COMANDA_TTL_MINUTES * 60 * 1000 };
        setComanda(refreshed);
        return refreshed;
      }
      const resp = await criarComandaPublic(slug, { mesa: mesaParam, nome: profile.nome, telefone: profile.telefone });
      const normalized = {
        ...resp,
        comanda_id: resp?.comanda_id || resp?.id,
        id: resp?.comanda_id || resp?.id,
        status: resp?.status || 'aberta',
        expiresAt: Date.now() + COMANDA_TTL_MINUTES * 60 * 1000,
      };
      setComanda(normalized);
      return normalized;
    } catch (e) {
      console.error('Erro ao criar/comanda', e);
      return null;
    }
  }, [slug, mesaParam, profile, clearSessao, tokenParam]);
  const currentComandaId = comanda?.comanda_id || comanda?.id;
  const addToHistorico = useCallback((pedidoSnap) => {
    if (!pedidoSnap?.id) return;
    if (currentComandaId && pedidoSnap.comanda_id && String(pedidoSnap.comanda_id) !== String(currentComandaId)) return;
    setHistoricoPedidos((list) => {
      const exists = list.some((p) => p.id === pedidoSnap.id);
      if (exists) return list;
      return [...list, pedidoSnap];
    });
  }, [currentComandaId]);
  const historicoAtual = useMemo(() => {
      // 1. Começa com o histórico já salvo
      let lista = [...historicoPedidos];

      // 2. Se o pedido atual (em memória) não for "aberto", ele teoricamente deve aparecer na lista de pedidos
      if (pedido?.status && pedido.status !== 'aberto') {
        // CORREÇÃO: Só adiciona o pedido atual se ele AINDA NÃO ESTIVER na lista do histórico
        // Isso evita a duplicação visual quando você acaba de enviar o pedido
        const jaExiste = lista.some((p) => String(p.id) === String(pedido.id));
        if (!jaExiste) {
          lista.push(pedido);
        }
      }

      // 3. Filtra apenas os pedidos da comanda atual (se houver comanda)
      if (!currentComandaId) return lista;
      return lista.filter((p) => String(p.comanda_id || p.id) === String(currentComandaId));
    }, [historicoPedidos, pedido, currentComandaId]);

  // Expira por tempo (cliente) e valida status
  useEffect(() => {
    if (!comanda) return undefined;
    const tick = setInterval(() => {
      if (comanda.expiresAt && comanda.expiresAt <= Date.now()) {
        if (!tokenParam) {
          clearSessao();
          setShowComandaDialog(true);
          toast.info('Comanda encerrada. Informe seu nome para abrir uma nova.', { autoClose: 4000 });
        } else {
          setCurrentTab('pedidos');
        }
      }
    }, 60 * 1000);
    return () => clearInterval(tick);
  }, [comanda, clearSessao, tokenParam]);

  // Valida status/expiração da comanda
  useEffect(() => {
    if (!hydrated || !comanda) return;
    const expired = comanda.expiresAt && comanda.expiresAt <= Date.now();
    const closed = !isComandaAtiva(comanda);
    if (expired || closed) {
      if (!tokenParam) {
        clearSessao();
        setShowComandaDialog(true);
        toast.info('Comanda encerrada. Informe seu nome para abrir uma nova.', { autoClose: 4000 });
      } else {
        setCurrentTab('pedidos');
      }
      return;
    }
  }, [hydrated, comanda, slug, clearSessao, tokenParam]);

  useEffect(() => {
    if (!hydrated || !comanda?.token) return undefined;
    const interval = setInterval(async () => {
      try {
        const info = await getComandaPublic(slug, comanda.token);
        const normalized = {
          ...info,
          token: comanda.token,
          comanda_id: info?.comanda_id || info?.id,
          id: info?.comanda_id || info?.id,
          status: info?.status || comanda.status,
          expiresAt: comanda.expiresAt,
        };
        if (!isComandaAtiva(normalized)) {
          clearSessao();
          setShowComandaDialog(true);
          return;
        }
        setComanda((prev) => {
          const same =
            prev &&
            prev.id === normalized.id &&
            prev.token === normalized.token &&
            prev.status === normalized.status &&
            prev.mesa === normalized.mesa;
          return same ? prev : normalized;
        });
      } catch {
        // silêncio
      }
    }, 120 * 1000); // a cada 2 minutos
    return () => clearInterval(interval);
  }, [hydrated, comanda?.token, slug, clearSessao, comanda?.status, comanda?.expiresAt]);
  const refreshPedido = useCallback(async (pedidoId) => {
    if (!pedidoId) return;
    try {
      const data = await getPedidoPublic(slug, pedidoId);
      const normalizado = normalizePedidoFromApi(data);
      setPedido(normalizado);
      if (normalizado.status === 'aberto') {
        setCarrinho(normalizado.itens || []);
      } else {
        addToHistorico(normalizado);
        setCarrinho([]);
      }
    } catch (err) {
      console.error('Erro ao atualizar pedido público', err);
    }
  }, [slug, addToHistorico]);
  const selectCardapio = useCallback(async (id) => {
    setSelectedCardapioId(id);
    setCategorias([]);
    setItens({});
    try {
      const cats = await listPublicCategorias(slug, id, canalSelecionado);
      setCategorias((cats || []).sort(sortByOrder));

      const itemsByCat = {};

      for (const cat of cats || []) {
        let list = await listPublicItens(slug, cat.id, canalSelecionado);
        list = (list || []).filter((it) => it.ativo);
        const sortedList = list.sort(sortByOrder);

        itemsByCat[cat.id] = sortedList;
      }
      setItens(itemsByCat);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      console.error('Erro ao carregar dados do cardápio:', e);
      setError('Não foi possível carregar os detalhes do cardápio.');
    }
  }, [slug, canalSelecionado]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getPublicRestaurante(slug);
        setRestaurante(res);

        const cards = await listPublicCardapios(slug, canalSelecionado);
        const sorted = (cards || []).sort(sortByOrder);
        setCardapios(sorted);

        if (sorted?.length) {
          const visible = sorted.filter((c) => c?.ativo !== false);
          const now = new Date();
          const available = visible.filter((c) => isCardapioAvailable(c, now));
          const visibleOutsideSchedule = visible.filter(
            (c) => !isCardapioAvailable(c, now)
              && c?.disponibilidade?.mostrarForaDoHorario !== false
          );
          const selectable = [...available, ...visibleOutsideSchedule];
          const first = selectable[0] || null;
          const target = cardapioIdParam
            ? selectable.find((c) => String(c.id) === String(cardapioIdParam)) || first
            : first;
          if (target?.id) {
            await selectCardapio(target.id);
          }
        }
      if (mesaParam) {
        try {
          const com = await ensureComanda();
          const comActive = com && isComandaAtiva(com) && (!com.expiresAt || com.expiresAt > Date.now());
          if (!com) {
            if (tokenParam && !profileDone) {
              setShowComandaDialog(true);
              setCarrinho([]);
              setPedido({ id: null, mesa: null, itens: [], status: 'aberto' });
            }
            return;
          }
          if (!comActive && tokenParam) {
            setCurrentTab('pedidos');
            setCarrinho([]);
            return;
          }
          const p = await criarOuObterPedidoPublic(slug, { mesa: mesaParam, comanda_id: com?.comanda_id || com?.id });
          if (p?.id) {
            const normalizado = normalizePedidoFromApi(p);
            if (normalizado.status === 'aberto') {
              setCarrinho(normalizado.itens || []);
            } else {
              addToHistorico(normalizado);
              setCarrinho([]);
            }
            setPedido(normalizado);
            if (normalizado.status !== 'aberto') {
              window.localStorage.removeItem('publicCarrinho');
            }
          }
        } catch (errPedido) {
          console.error('Erro ao iniciar pedido público', errPedido);
        }
      }
      } catch (e) {
        console.error('Erro na inicialização (público):', e);
        setError('Não foi possível carregar o menu. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };
    if (slug && hydrated) {
      init();
    }
  // addToHistorico não entra na lista para evitar re-renders infinitos
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCardapio, slug, cardapioIdParam, mesaParam, hydrated, ensureComanda, tokenParam, profileDone, canalSelecionado]);

  const banners = useMemo(() => {
    const fromRest = restaurante?.banners || restaurante?.bannerUrls || [];
    if (Array.isArray(fromRest) && fromRest.length) return fromRest;
      const firstImg = cardapios?.find((c) => c.foto_url)?.foto_url || null;
      return firstImg ? [firstImg] : [];
    }, [restaurante, cardapios]);
  const layout = restaurante?.cardapio_layout || 'modern';
  const theme = themes[layout] || themes.modern;
  const visibleCardapios = useMemo(
    () => (cardapios || []).filter((c) => c?.ativo !== false),
    [cardapios]
  );
  const availableCardapios = useMemo(
    () => visibleCardapios.filter((c) => isCardapioAvailable(c, availabilityNow)),
    [visibleCardapios, availabilityNow]
  );
  const visibleOutsideSchedule = useMemo(
    () => visibleCardapios.filter(
      (c) => !isCardapioAvailable(c, availabilityNow)
        && c?.disponibilidade?.mostrarForaDoHorario !== false
    ),
    [visibleCardapios, availabilityNow]
  );
  const cardapiosParaTabs = useMemo(
    () => [...availableCardapios, ...visibleOutsideSchedule],
    [availableCardapios, visibleOutsideSchedule]
  );
  const showCardapioTabs = cardapiosParaTabs.length > 1;
  const selectedCardapio = useMemo(
    () => cardapiosParaTabs.find((c) => c.id === selectedCardapioId) || cardapiosParaTabs[0] || null,
    [cardapiosParaTabs, selectedCardapioId]
  );
  const selectedCardapioAvailable = selectedCardapio
    ? isCardapioAvailable(selectedCardapio, availabilityNow)
    : false;
  const aceitaPedido = !!selectedCardapio?.aceita_pedido && selectedCardapioAvailable;
  const shouldShowMenu = currentTab === 'home';
  const shouldShowHistorico = currentTab === 'pedidos' && aceitaPedido && !!mesaParam;
  const shouldShowCarrinho = currentTab === 'carrinho' && aceitaPedido && !!mesaParam;
  const heroImage = restaurante?.cardapio_bg_image_url || (banners.length ? banners[bannerIndex] : null);
  const heroColor = restaurante?.cardapio_bg_color || null;
  const showBannerNavigation = !restaurante?.cardapio_bg_image_url && banners.length > 1;

  const goPrev = () => setBannerIndex((i) => (i - 1 + banners.length) % banners.length);
  const goNext = () => setBannerIndex((i) => (i + 1) % banners.length);

  const comandaAtiva = comanda && isComandaAtiva(comanda) && (!comanda.expiresAt || comanda.expiresAt > Date.now());
  const comandaFechadaToken = tokenParam && comanda && !comandaAtiva;

  useEffect(() => {
    if (selectedCardapioId == null) return;
    const stillVisible = cardapiosParaTabs.some((cardapio) => cardapio.id === selectedCardapioId);
    if (stillVisible) return;

    const nextCardapio = cardapiosParaTabs[0];
    if (nextCardapio?.id) {
      selectCardapio(nextCardapio.id);
      return;
    }

    setSelectedCardapioId(null);
    setCategorias([]);
    setItens({});
  }, [cardapiosParaTabs, selectCardapio, selectedCardapioId]);

  const handleFecharComanda = async () => {
    if (!comanda || !mesaParam) return;
    try {
      await fecharComandaPublic(slug, { token: tokenParam || comanda.token, comanda_id: comanda.comanda_id || comanda.id });
      toast.success('Comanda encerrada.');
      setComanda((prev) => ({ ...(prev || {}), status: 'finalizada' }));
      setCarrinho([]);
      setCurrentTab('pedidos');
      setShowComandaDialog(false);
    } catch (e) {
      console.error('Erro ao fechar comanda', e);
      toast.error('Não foi possível fechar a comanda.');
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (comandaFechadaToken) {
      setCurrentTab('pedidos');
    }
  }, [comandaFechadaToken]);

  const openItem = async (item) => {
    if (!comandaAtiva && aceitaPedido && mesaParam) {
      toast.info('Comanda encerrada. Abra uma nova para continuar.');
      return;
    }
    setSelectedItem(item);
    try {
      const [comps, groups] = await Promise.all([
        listPublicItemComplementos(slug, item.id),
        listPublicItemOptionGroups(slug, item.id, canalSelecionado),
      ]);
      setSelectedItemComplementos(comps || []);
      setSelectedItemOptionGroups(groups || []);
    } catch (e) {
      console.error('Erro ao carregar opcoes do item', e);
      setSelectedItemComplementos([]);
      setSelectedItemOptionGroups([]);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
        <Typography ml={2}>Carregando o menu...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh" px={3}>
        <Paper elevation={2} sx={{ p: 3, maxWidth: 520, textAlign: 'center' }}>
          <Typography variant="h6" mb={1} color="error">Ops, algo deu errado</Typography>
          <Typography color="text.secondary">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  const shouldShowTabs = !(comandaFechadaToken && aceitaPedido && mesaParam);
  const shouldShowFloatingCategorias = shouldShowMenu && categorias.length > 0;

  const handleCategoriaJump = (categoriaId) => {
    const target = document.getElementById(`cat-${categoriaId}`);
    if (!target) return;
    const stickyOffset = 140;
    const top = target.getBoundingClientRect().top + window.scrollY - stickyOffset;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        bgcolor: theme.background,
        position: 'relative',
        fontFamily: theme.fontFamily,
        backgroundImage: layout === 'retro'
          ? `repeating-linear-gradient(0deg, transparent, transparent 38px, rgba(31,79,191,0.06) 38px, rgba(31,79,191,0.06) 40px),
             repeating-linear-gradient(90deg, transparent, transparent 38px, rgba(31,79,191,0.06) 38px, rgba(31,79,191,0.06) 40px)`
          : 'none',
      }}
    >
      <IconButton onClick={() => navigate(-1)} aria-label="Voltar" sx={{ position: 'absolute', top: 8, left: 8 }}>
        <ArrowBackIcon />
      </IconButton>

      {/* HERO */}
        <Box sx={{
          position: 'relative', height: heroHeight, borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden', mx: { xs: 0, sm: 2 }, mt: { xs: 0, sm: 2 },
        backgroundColor: heroImage ? (heroColor || 'transparent') : (heroColor || 'grey.200'),
        backgroundImage: heroImage
          ? `url("${heroImage}")`
          : heroColor
            ? 'none'
            : fallbackHeroGradient,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover', backgroundPosition: 'center',
        border: layout === 'retro' ? '3px solid #1f4fbf' : 'none',
        boxShadow: layout === 'retro' ? '0 10px 30px rgba(28,39,76,0.16)' : 'none',
      }}>
        {heroImage && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: theme.heroOverlay,
              pointerEvents: 'none',
            }}
          />
        )}
        {(restaurante?.cardapio_logo_url || restaurante?.foto_url) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Avatar
              src={restaurante.cardapio_logo_url || restaurante.foto_url}
              alt={restaurante?.nome || 'Logo do restaurante'}
              sx={{
                width: { xs: 96, sm: 112, md: 128 },
                height: { xs: 96, sm: 112, md: 128 },
                borderRadius: '50%',
                border: '6px solid rgba(255,255,255,0.9)',
                boxShadow: '0 16px 38px rgba(0,0,0,0.24)',
                bgcolor: 'white',
              }}
            />
          </Box>
        )}
        {showBannerNavigation && (
          <>
            <IconButton onClick={goPrev} aria-label="Banner anterior" sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' } }} size="small"><ArrowBackIosNewIcon fontSize="small" /></IconButton>
            <IconButton onClick={goNext} aria-label="Próximo banner" sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' } }} size="small"><ArrowForwardIosIcon fontSize="small" /></IconButton>
          </>
        )}
      </Box>

      {/* NOME E LOGO */}
      <Stack direction="row" spacing={2} px={{ xs: 2, sm: 3 }} mt={3} mb={2} alignItems="center">
        {(restaurante?.cardapio_logo_url || restaurante?.foto_url) && (
          <Avatar src={restaurante.cardapio_logo_url || restaurante.foto_url} alt={restaurante?.nome || 'Logo do restaurante'} sx={{ width: 60, height: 60, border: layout === 'retro' ? '3px solid #f26b3a' : 'none' }} />
        )}
        <Typography variant="h4" fontWeight={700} sx={{ color: theme.textPrimary, letterSpacing: layout === 'retro' ? '0.02em' : 0, textTransform: layout === 'retro' ? 'uppercase' : 'none' }}>
          {restaurante?.nome || 'Nome do Restaurante'}
        </Typography>
        {aceitaPedido && mesaParam && (
          <Box sx={{ ml: 'auto' }}>
            {comandaFechadaToken ? (
              <Chip label="Comanda fechada" color="error" variant="outlined" />
            ) : (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setShowFecharComandaConfirm(true)}
              >
                Fechar comanda
              </Button>
            )}
          </Box>
        )}
      </Stack>
      {/* {aceitaPedido && !mesaParam && (
        <Box px={{ xs: 2, sm: 3 }} mb={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Typography fontWeight={700} color="primary.main">Pedidos via QR Code</Typography>
            <Typography color="text.secondary">
              Para fazer pedidos, escaneie o QR Code disponível na mesa. Você poderá acompanhar a comanda em tempo real.
            </Typography>
          </Paper>
        </Box>
      )} */}

      {/* SELEÇÃO DE CARDÁPIOS (oculta se só houver 1) */}
      {shouldShowMenu && showCardapioTabs && shouldShowTabs && (
        <Box sx={{ px: { xs: 2, sm: 3 }, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 0 }}>
            {cardapiosParaTabs.map((c) => {
              const selected = selectedCardapioId === c.id;
              const availableNow = isCardapioAvailable(c, availabilityNow);
              return (
                <Chip
                  key={c.id}
                  onClick={() => selectCardapio(c.id)}
                  label={`${getNome(c)}${availableNow ? '' : ' · fora do horário'}`}
                  clickable
                  sx={{
                    py: 2.5, px: 1.5, fontSize: '1rem', borderRadius: '8px 8px 0 0',
                    bgcolor: selected ? 'white' : 'transparent',
                    color: selected ? 'primary.main' : 'text.secondary',
                    border: selected ? '1px solid' : 'none', borderColor: 'divider', borderBottom: 'none',
                    fontWeight: selected ? 600 : 400, transform: selected ? 'translateY(1px)' : 'none'
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      {/* NAVEGAÇÃO DE CATEGORIAS */}
      {shouldShowMenu && availableCardapios.length === 0 && visibleOutsideSchedule.length > 0 && (
        <Box px={{ xs: 2, sm: 3 }} mb={2}>
          <Alert severity="info">
            Estamos fora dos horários configurados. Os cardápios abaixo estão disponíveis apenas para consulta.
          </Alert>
        </Box>
      )}

      {shouldShowMenu && selectedCardapio && !selectedCardapioAvailable && availableCardapios.length > 0 && (
        <Box px={{ xs: 2, sm: 3 }} mb={2}>
          <Alert severity="info">
            Este cardápio está fora do horário configurado e não aceita pedidos agora.
          </Alert>
        </Box>
      )}

      {shouldShowMenu && visibleCardapios.length > 0 && cardapiosParaTabs.length === 0 && (
        <Box px={{ xs: 2, sm: 3 }} mb={2}>
          <Alert severity="info">Nenhum cardápio está disponível neste horário.</Alert>
        </Box>
      )}

      {shouldShowFloatingCategorias && (
        <Box
          sx={{
            position: 'sticky',
            top: { xs: 8, sm: 12 },
            zIndex: 9,
            px: { xs: 2, sm: 3 },
            mb: 3,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              px: 1,
              py: 1,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.96)',
              backdropFilter: 'blur(6px)',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
            }}
          >
            <Stack direction="row" spacing={1} flexWrap="nowrap" sx={{ overflowX: 'auto', pb: 0.25 }}>
              {categorias.map((cat) => (
                <Chip
                  key={cat.id}
                  label={getNome(cat)}
                  clickable
                  onClick={() => handleCategoriaJump(cat.id)}
                  variant="outlined"
                  sx={{
                    borderRadius: 99,
                    fontWeight: 700,
                    bgcolor: 'white',
                    color: layout === 'retro' ? theme.accent : undefined,
                    borderColor: layout === 'retro' ? theme.accent : undefined,
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </Box>
      )}

      {!visibleCardapios.length && (
        <Box px={{ xs: 2, sm: 3 }} mb={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Typography fontWeight={700}>
              Nenhum cardápio disponível para {channelLabel}
            </Typography>
            <Typography color="text.secondary">
              Revise as publicações ativas no OneMenu.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* ITENS POR CATEGORIA */}
      {shouldShowMenu && (
        <Box px={{ xs: 2, sm: 3 }} pb={4}>
          {categorias.map((cat) => {
            const lista = itens[cat.id] || [];
            return (
              <Box key={cat.id} id={`cat-${cat.id}`} mb={4}>
                <Box sx={{
                  ...categoryHeaderSx,
                  bgcolor: theme.categoryBg,
                  borderColor: theme.categoryBorder,
                  borderWidth: 2,
                  borderStyle: 'solid',
                  boxShadow: layout === 'retro' ? '0 8px 14px rgba(0,0,0,0.08)' : 'none',
                }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: theme.textPrimary, letterSpacing: layout === 'retro' ? '0.04em' : 0 }}>
                    {getNome(cat)}
                  </Typography>
                </Box>

                <Stack divider={<Divider />}>
                  {lista.map((item) => (
                    <DefaultItem key={item.id} item={item} onClick={() => openItem(item)} theme={theme} layout={layout} />
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}

      {shouldShowHistorico && (
        <Box px={{ xs: 2, sm: 3 }} pb={4} ref={historicoRef}>
          <Paper variant="outlined" sx={{ p: 2.5, borderColor: theme.categoryBorder, mb: 2 }}>
            <Typography variant="h6" fontWeight={800} mb={1}>
              Pedidos enviados – Mesa {mesaParam}
            </Typography>
            {historicoAtual.length === 0 && (
              <Typography color="text.secondary">Nenhum pedido enviado ainda.</Typography>
            )}
            <Stack spacing={1.5}>
              {historicoAtual
                .sort((a, b) => (b.id || 0) - (a.id || 0))
                .map((ped) => (
                  <Paper key={ped.id} variant="outlined" sx={{ p: 1.5, borderColor: theme.categoryBorder }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography fontWeight={800}>Pedido #{ped.id || '-'}</Typography>
                      <Chip size="small" label={ped.status || 'enviado'} color={ped.status === 'enviado' ? 'primary' : 'default'} />
                    </Stack>
                    <Stack spacing={0.75}>
                      {(ped.itens || []).map((it) => (
                        <Box key={it.id} sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 1, backgroundColor: 'background.paper' }}>
                          <Typography fontWeight={700}>{it.quantidade}x {it.nome}</Typography>
                          {it.complementos?.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              Compl.: {it.complementos.map((c) => c.nome).join(', ')}
                            </Typography>
                          )}
                          {it.opcoes?.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              Escolhas: {it.opcoes.map((option) => option.nome).join(', ')}
                            </Typography>
                          )}
                          {it.observacao && <Typography variant="body2" color="text.secondary">Obs: {it.observacao}</Typography>}
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                ))}
            </Stack>
          </Paper>
        </Box>
      )}

      {shouldShowCarrinho && (
        <Box px={{ xs: 2, sm: 3 }} pb={8} ref={carrinhoRef}>
          <Paper variant="outlined" sx={{ p: 2.5, borderColor: theme.categoryBorder, background: 'linear-gradient(180deg, #ffffff 0%, #f5f7fb 100%)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" fontWeight={800}>Carrinho – Mesa {mesaParam}</Typography>
              <Typography fontWeight={800} color="primary.main">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                  calcularTotal({ itens: carrinho })
                )}
              </Typography>
            </Stack>
            {(carrinho || []).length === 0 && (
              <Typography color="text.secondary">Nenhum item no carrinho.</Typography>
            )}
            <Stack spacing={1.5}>
              {(carrinho || []).map((it) => (
                <Box key={it.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, backgroundColor: 'white' }}>
                  <Typography fontWeight={700}>{it.nome}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                    <Typography variant="body2" color="text.secondary">Qtd:</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        if (!it.pedido_item_id) return;
                        const novaQtd = Math.max(1, (it.quantidade || 1) - 1);
                        try {
                          await atualizarItemPedidoPublic(slug, pedido.id, it.pedido_item_id, { quantidade: novaQtd });
                          setCarrinho((list) => list.map((x) => (x.id === it.id ? { ...x, quantidade: novaQtd } : x)));
                        } catch (e) {
                          console.error('Erro ao atualizar quantidade', e);
                          alert('Não foi possível atualizar.');
                        }
                      }}
                    >-</Button>
                    <Typography variant="body2" fontWeight={700}>{it.quantidade}</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        if (!it.pedido_item_id) return;
                        const novaQtd = (it.quantidade || 1) + 1;
                        try {
                          await atualizarItemPedidoPublic(slug, pedido.id, it.pedido_item_id, { quantidade: novaQtd });
                          setCarrinho((list) => list.map((x) => (x.id === it.id ? { ...x, quantidade: novaQtd } : x)));
                        } catch (e) {
                          console.error('Erro ao atualizar quantidade', e);
                          alert('Não foi possível atualizar.');
                        }
                      }}
                    >+</Button>
                  </Stack>
                  {it.complementos?.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Compl.: {it.complementos.map((c) => c.nome).join(', ')}
                    </Typography>
                  )}
                  {it.opcoes?.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Escolhas: {it.opcoes.map((option) => option.nome).join(', ')}
                    </Typography>
                  )}
                  {it.observacao && <Typography variant="body2" color="text.secondary">Obs: {it.observacao}</Typography>}
                  <Box textAlign="right" mt={1}>
                    <Button
                      size="small"
                      color="error"
                      onClick={async () => {
                        try {
                          if (it.pedido_item_id) {
                            await removerItemPedidoPublic(slug, pedido.id, it.pedido_item_id);
                          }
                          setCarrinho((list) => list.filter((x) => x.id !== it.id));
                        } catch (e) {
                          console.error('Erro ao remover item do pedido', e);
                          toast.error('Não foi possível remover o item.');
                        }
                      }}
                    >
                      Remover
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              disabled={!pedido.id || (carrinho || []).length === 0 || !comandaAtiva}
              onClick={async () => {
                try {
              await enviarPedidoPublic(slug, pedido.id);
              await refreshPedido(pedido.id);
              setPedido((p) => {
                const novo = { ...p, status: 'enviado' };
                window.localStorage.setItem('publicPedido', JSON.stringify(novo));
                return novo;
              });
              setCarrinho([]);
              setCurrentTab('pedidos');
              historicoRef.current?.scrollIntoView({ behavior: 'smooth' });
              toast.success('Pedido enviado!');
            } catch (e) {
              console.error('Erro ao enviar pedido', e);
              toast.error('Não foi possível enviar o pedido.');
            }
              }}
            >
              Enviar pedido
            </Button>
          </Paper>
        </Box>
      )}

      {aceitaPedido && mesaParam && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'white',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -6px 18px rgba(0,0,0,0.1)',
            zIndex: 10,
            display: 'flex',
          }}
        >
          {[
            { key: 'home', label: 'Início', icon: <HomeIcon fontSize="small" />, ref: headerRef },
            { key: 'carrinho', label: `Carrinho (${carrinho?.length || 0})`, icon: <ShoppingCartIcon fontSize="small" />, ref: carrinhoRef },
            { key: 'pedidos', label: 'Pedidos', icon: <ListAltIcon fontSize="small" />, ref: historicoRef },
          ].map((tab, idx) => (
            <Box
              key={tab.label}
              sx={{
                flex: 1,
                py: 1.25,
                textAlign: 'center',
                color: currentTab === tab.key ? 'primary.main' : 'text.secondary',
                borderBottom: currentTab === tab.key ? '3px solid' : '3px solid transparent',
                borderColor: currentTab === tab.key ? 'primary.main' : 'transparent',
              }}
            >
              <Box onClick={() => {
                if (comandaFechadaToken && tab.key !== 'pedidos') return;
                setCurrentTab(tab.key);
                tab.ref?.current?.scrollIntoView({ behavior: 'smooth' });
              }} sx={{ cursor: comandaFechadaToken && tab.key !== 'pedidos' ? 'not-allowed' : 'pointer', opacity: comandaFechadaToken && tab.key !== 'pedidos' ? 0.5 : 1 }}>
                <Box display="flex" justifyContent="center">{tab.icon || null}</Box>
                <Typography variant="body2">{tab.label}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={showFecharComandaConfirm} onClose={() => setShowFecharComandaConfirm(false)} fullWidth maxWidth="xs">
        <DialogTitle>Fechar comanda</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Deseja fechar esta comanda?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFecharComandaConfirm(false)}>Voltar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              setShowFecharComandaConfirm(false);
              await handleFecharComanda();
            }}
          >
            Fechar comanda
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de comanda (nome/telefone) */}
      <Dialog
        open={showComandaDialog && !!mesaParam && !!tokenParam && !profileDone && comandaAtiva}
        onClose={(_, reason) => {
          if (!profile.nome.trim()) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            setShowComandaDialog(false);
          }
        }}
        disableEscapeKeyDown={!profile.nome.trim()}
      >
        <DialogTitle>Iniciar comanda</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Informe seus dados para vincular esta comanda à mesa {mesaParam}.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Nome"
              value={profile.nome}
              onChange={(e) => setProfile((s) => ({ ...s, nome: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Telefone (opcional)"
              value={profile.telefone}
              onChange={(e) => setProfile((s) => ({ ...s, telefone: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={!profile.nome.trim()}
            onClick={() => {
              if (!profile.nome.trim()) return;
              setShowComandaDialog(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!profile.nome.trim()}
            onClick={async () => {
              await ensureComanda();
              if (profileFlagKey) {
                try {
                  window.localStorage.setItem(profileFlagKey, '1');
                  setProfileDone(true);
                } catch {
                  // ignore
                }
              }
              setShowComandaDialog(false);
            }}
          >
            Iniciar comanda
          </Button>
        </DialogActions>
      </Dialog>

      {/* POPUP */}
      <ItemPopup
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onAddToCart={async (payload) => {
          if (!aceitaPedido || !mesaParam) return;
          try {
            let targetPedido = pedido;
            if (!targetPedido.id || targetPedido.status !== 'aberto') {
              const com = await ensureComanda();
              const novo = await criarOuObterPedidoPublic(slug, { mesa: mesaParam, comanda_id: com?.comanda_id || com?.id });
              const normalizado = normalizePedidoFromApi(novo);
              if (targetPedido.id && targetPedido.status !== 'aberto') {
                addToHistorico(targetPedido);
              }
              setPedido(normalizado);
              setCarrinho([]);
              targetPedido = normalizado;
            }
            const res = await adicionarItemPedidoPublic(slug, targetPedido.id, {
              item_id: payload.id,
              quantidade: payload.quantidade || 1,
              observacao: payload.observacao || '',
              complementos: (payload.complementosSelecionados || []).map((c) => ({ id: c.id, preco: c.preco || 0 })),
              opcoes: (payload.opcoesSelecionadas || []).map((option) => ({
                option_id: option.id,
                quantidade: 1,
              })),
              canal: canalSelecionado,
            });
            if (res?.id) {
              const normalizado = normalizePedidoItem(res);
              setCarrinho((list) => [...list, normalizado]);
              setCurrentTab('carrinho');
              carrinhoRef.current?.scrollIntoView({ behavior: 'smooth' });
            }
          } catch (e) {
            console.error('Erro ao adicionar item ao pedido', e);
            const message = e?.response?.data?.message
              || e?.response?.data?.error
              || 'Não foi possível adicionar o item. Atualize o cardápio e tente novamente.';
            toast.error(message);
          }
        }}
        complementos={selectedItemComplementos}
        optionGroups={selectedItemOptionGroups}
        aceitaPedido={aceitaPedido && !!mesaParam}
      />
    </Box>
  );
}
